import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon, Dropdown } from "semantic-ui-react";
import { toast } from "sonner";

import {
  PLAYBACK_BLOCKED_MESSAGE,
  PLAYBACK_FAILED_MESSAGE,
} from "constants/AudioPlayback";
import CustomDropdown from "./CustomDropdown";
import styles from "./AudioElement.module.scss";

const DURATION_PLACEHOLDER = "--:--";

// helpers
const formatTime = seconds => {
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const rest = String(total % 60).padStart(2, "0");
  return hours ? `${hours}:${minutes}:${rest}` : `${minutes}:${rest}`;
};
const getPosition = el => el.getBoundingClientRect().left;

const AudioElement = ({ src, id, durationSeconds, wide = false }) => {
  const audio = useRef();
  const playhead = useRef();
  const timeline = useRef();
  const playRequested = useRef(false);
  const [state, setState] = useState("stopped");
  const [metadataDuration, setMetadataDuration] = useState(null);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [muted, setMuted] = useState(false);

  // Nothing is loaded until the first play, so the reported length carries the
  // readout until the recording's own metadata replaces it.
  const duration =
    metadataDuration ??
    (durationSeconds ? formatTime(durationSeconds) : DURATION_PLACEHOLDER);

  const clickPercent = (event, timelineWidth) =>
    (event.clientX - getPosition(timeline.current)) / timelineWidth;

  const movePlayhead = useCallback(event => {
    const newMargLeft = event.clientX - getPosition(timeline.current);
    const timelineWidth =
      timeline.current.offsetWidth - playhead.current.offsetWidth;
    const seekTo = audio.current.duration * clickPercent(event, timelineWidth);

    // Duration is NaN until the recording loads, and an unlaid-out timeline
    // makes the fraction infinite. Assigning either to currentTime throws.
    if (!Number.isFinite(seekTo)) return;

    playhead.current.style.marginLeft = newMargLeft + "px";
    audio.current.currentTime = seekTo;
  }, []);

  const initializePlayer = () => {
    const loaded = audio.current.duration;
    // A resource whose length the browser cannot determine reports Infinity
    // or NaN, and the reported length is a better readout than either.
    if (Number.isFinite(loaded)) setMetadataDuration(formatTime(loaded));
  };

  const failPlayback = useCallback(
    (message = PLAYBACK_FAILED_MESSAGE) => {
      audio.current.pause();
      setState("paused");
      playRequested.current = false;
      // A shared id replaces the standing toast rather than stacking one per
      // retry, and per row when a table's recordings are all unreachable.
      toast.error(message, { id: `audio-playback-${id}` });
    },
    [id]
  );

  const playAudio = () => {
    if (!audio.current.paused) {
      audio.current.pause();
      setState("paused");
      // The browser can keep buffering after a pause, and a failure then has
      // no listener to tell.
      playRequested.current = false;
      return;
    }
    playRequested.current = true;
    setState("playing");
    audio.current.play().catch(error => {
      // play() rejects with AbortError when a pause or a src swap interrupts
      // the pending request. That is not a broken recording, so stay quiet.
      if (error?.name === "AbortError") return;
      failPlayback(
        error?.name === "NotAllowedError"
          ? PLAYBACK_BLOCKED_MESSAGE
          : PLAYBACK_FAILED_MESSAGE
      );
    });
  };

  const timeUpdate = () => {
    const playPercent =
      99 * (audio.current.currentTime / audio.current.duration);
    setCurrentTime(formatTime(audio.current.currentTime));
    playhead.current.style.marginLeft = playPercent + "%";
  };

  const setPlaybackSpeed = speed => (audio.current.playbackRate = speed);

  useEffect(() => {
    const audioElem = audio.current;
    const timelineElem = timeline.current;

    const ended = () => {
      setState("stopped");
      setCurrentTime("00:00");
      playhead.current.style.marginLeft = "0%";
      playRequested.current = false;
    };
    // A media error can also arrive without anyone asking to listen, from a
    // src swap or a stalled load. Only the requested playback has a user to
    // tell about it.
    const errored = () => {
      if (playRequested.current) failPlayback();
    };

    audioElem.addEventListener("loadedmetadata", initializePlayer, false);
    audioElem.addEventListener("timeupdate", timeUpdate, false);
    audioElem.addEventListener("ended", ended, false);
    audioElem.addEventListener("error", errored, false);
    timelineElem.addEventListener("click", movePlayhead, false);
    return () => {
      timelineElem.removeEventListener("click", movePlayhead);
      audioElem.removeEventListener("loadedmetadata", initializePlayer);
      audioElem.removeEventListener("timeupdate", timeUpdate);
      audioElem.removeEventListener("ended", ended);
      audioElem.removeEventListener("error", errored);
    };
  }, [movePlayhead, failPlayback, src]);

  useEffect(() => {
    audio.current.muted = muted;
  }, [muted]);

  return (
    <div
      className={styles.audioplayer}
      style={{ width: wide ? "460px" : "300px" }}
    >
      {/* preload="none" keeps a table of players from fetching every
          recording at once, which the storage host rejects under that burst.
          The type comes from the response; the former <source type> hints
          made the browser attempt containers the file never was. */}
      <audio id={id} ref={audio} src={src} preload="none" />
      <Icon name={state === "playing" ? "pause" : "play"} onClick={playAudio} />
      <span>
        {currentTime} / {duration}{" "}
      </span>
      <div className={styles.timeline} ref={timeline}>
        <div className={styles.playhead} ref={playhead}></div>
      </div>
      <Icon
        onClick={() => setMuted(!muted)}
        name={muted ? "volume off" : "volume up"}
      />
      <CustomDropdown icon="forward">
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => setPlaybackSpeed(1)} text="1x" />
          <Dropdown.Item onClick={() => setPlaybackSpeed(1.5)} text="1.5x" />
          <Dropdown.Item onClick={() => setPlaybackSpeed(2)} text="2x" />
        </Dropdown.Menu>
      </CustomDropdown>
      <a
        style={{ color: "black" }}
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        <Icon name="download" />
      </a>
    </div>
  );
};

export default AudioElement;
